import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminDashboardCount, getadminWeekOverview, getUsersList, toggleUserActive } from '../../redux/reduxSlices/adminSlice';
import { assignOrder, getAllOrders } from '../../redux/reduxSlices/ordersSlice';
import { BarChart3, CheckCircle, Clock, FileCheck, FileText, Music, Music2, Plus, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import { getMusicCreators } from '../../redux/reduxSlices/userProfileSlice';

// Import Recharts components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { adminId } from '../../utils/util';

function AdminDashboard({ user, onLogout }) {

  const {
    adminLoading,
    adminFetchError,
    adminDashboardCountResponse,
    adminWeekOverviewResponse,
    usersListResponse,
    toggleUserResponse
  } = useSelector((state) => state.admin);

  const {
    loading,
    fetchError,
    ordersData,
  } = useSelector((state) => state.orders);
  const { musicCreators } = useSelector((state) => state.profile);
  const [copied, setCopied] = useState(false);


  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getAdminDashboardCount())
    dispatch(getadminWeekOverview())
    dispatch(getUsersList())
    dispatch(getAllOrders());
    dispatch(getMusicCreators());
  }, [])

  // Format data for Recharts
  const getChartData = () => {
    if (!adminWeekOverviewResponse?.days) return [];

    return adminWeekOverviewResponse.days.map(day => ({
      name: formatDate(day.day),
      fullDate: day.day,
      weekday: getWeekday(day.day),
      Received: day.total_received || 0,
      Completed: day.total_completed || 0,
      // You can add more data if needed
      Pending: (day.total_received || 0) - (day.total_completed || 0)
    }));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Link copied to clipboard!");

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy link");
    }
  };


  // Format tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{data.fullDate}</p>
          <p className="text-sm text-gray-600 mb-2">{data.weekday}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-gray-600">{entry.dataKey}:</span>
              </div>
              <span className="font-semibold text-gray-800">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Simple date formatter
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  // Get weekday
  const getWeekday = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('default', { weekday: 'short' });
  };

  const getAssignedToName = (id) => {
    if (!id || !usersListResponse?.music_creators) return "-";

    const user = usersListResponse.music_creators.find(
      (user) => user.user_pk === id
    );

    if (user) {
      return `${user.first_name} ${user.last_name}`;
    }

    return "-";
  };

  const assignTask = async (orderId, assigned_to) => {
    try {
      const payload = {
        order_id: orderId,
        user_pk: assigned_to
      };
      const response = await dispatch(assignOrder(payload)).unwrap();
      if (response.success) {
        await dispatch(getAllOrders());
        toast.success("Task assigned successfully");
      }
    } catch (error) {
      console.log("error", error);
      toast.error(error?.message || "Failed to assign task.");
    }
  };

  const statusConfig = [
    { value: "pending", label: "Pending", color: "bg-yellow-500", gradient: "from-yellow-400 to-yellow-600", icon: Clock },
    { value: "in_progress", label: "In Progress", color: "bg-blue-500", gradient: "from-blue-400 to-blue-600", icon: Music },
    { value: "lyrics_approval_pending", label: "Lyrics Approval Pending", color: "bg-orange-500", gradient: "from-orange-400 to-orange-600", icon: FileText },
    { value: "lyrics_approved", label: "Lyrics Approved", color: "bg-purple-500", gradient: "from-purple-400 to-purple-600", icon: FileCheck },
    { value: "music_approval_pending", label: "Music Approval Pending", color: "bg-indigo-500", gradient: "from-indigo-400 to-indigo-600", icon: Music2 },
    { value: "music_approved", label: "Music Approved", color: "bg-teal-500", gradient: "from-teal-400 to-teal-600", icon: CheckCircle },
    { value: "delivered", label: "Delivered", color: "bg-green-500", gradient: "from-green-400 to-green-600", icon: Truck }
  ];

  const StatusBadge = ({ status }) => {
    const config = statusConfig.find(s => s.value === status);
    const Icon = config?.icon || Clock;

    return (
      <div className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${config?.color ? `bg-${config.color.split('-')[1]}-100 text-${config.color.split('-')[1]}-800` : 'bg-gray-100 text-gray-800'
        }`}>
        <Icon className="w-3 h-3" />
        {config?.label || status}
      </div>
    );
  };

  // Sample data for stats cards
  const statsData = [
    { title: 'Total Orders', value: adminDashboardCountResponse?.counts?.total_orders, color: 'bg-blue-500' },
    { title: 'Orders Delivered', value: adminDashboardCountResponse?.counts?.delivered, color: 'bg-green-500' },
    { title: 'Orders Pending', value: adminDashboardCountResponse?.counts?.pending, color: 'bg-yellow-500' },
    {
      title: 'Orders Processing', value: (
        (adminDashboardCountResponse?.counts?.in_progress || 0) +
        (adminDashboardCountResponse?.counts?.lyrics_approval_pending || 0) +
        (adminDashboardCountResponse?.counts?.lyrics_approved || 0) +
        (adminDashboardCountResponse?.counts?.music_approval_pending || 0) +
        (adminDashboardCountResponse?.counts?.music_approved || 0)
      ), color: 'bg-orange-500'
    },
    { title: 'Sales Persons', value: adminDashboardCountResponse?.users?.sales_person, color: 'bg-purple-500' },
    { title: 'Music Creators', value: adminDashboardCountResponse?.users?.music_creator, color: 'bg-pink-500' },
  ];

  const navigate = useNavigate()

  const togglePersonStatus = async (person) => {
    try {
      const payload = {
        "user_pk": person.user_pk,
        "user_type": person.user_type,
        "is_active": !person.is_active
      }
      const response = await dispatch(toggleUserActive(payload)).unwrap()
      if (response.success) {
        await dispatch(getUsersList())
        toast.success("Status changed successfully!")
      }
    } catch (error) {
      console.log("error", error)
      toast.error("Something went wrong!");
    }
  };

  const handleViewMoreSales = () => {
    navigate("/admin-sales-persons-data")
  };

  const handleViewMoreMusic = () => {
    navigate("/admin-music-creator-data")
  };

  const handleViewMoreOrders = () => {
    navigate("/admin-order-data")
  };

  // Chart data
  const chartData = getChartData();

  return (
    <>
      <Header user={user} onLogout={onLogout} />
      <div className="min-h-screen py-4 md:px-7 max-w-[1800px] mx-auto ">

        {/* <button className="inline-flex items-center px-3 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 mt-2"
          onClick={() => navigate('/create-collage')} ><Plus className="w-4 h-4" />Create Collage
        </button> */}

        {/* Stats Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-6 mb-8 mt-4">
          {statsData.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
                <span className="text-white font-bold text-lg">
                  {stat.value}
                </span>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-2">
                {stat.title}
              </h3>
              <p className="text-2xl font-bold text-gray-800">
                {stat.value}
              </p>
            </div>
          ))}
        </div>

        {/* Recharts Bar Graph */}
        {adminWeekOverviewResponse?.days && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Last 7 Days Overview</h2>

            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 60,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f0f0f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    interval={0}
                  >
                    <label
                      value="Date"
                      offset={-20}
                      position="insideBottom"
                      style={{ textAnchor: 'middle', fill: '#6b7280' }}
                    />
                  </XAxis>
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    label={{
                      value: 'Number of Orders',
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle', fill: '#6b7280' }
                    }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                  />
                  <Legend
                    verticalAlign="top"
                    height={36}
                    wrapperStyle={{ paddingBottom: '20px' }}
                  />
                  <Bar
                    dataKey="Received"
                    name="Orders Received"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                    barSize={30}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill="#3b82f6"
                        className="hover:opacity-80 transition-opacity"
                      />
                    ))}
                  </Bar>
                  <Bar
                    dataKey="Completed"
                    name="Orders Completed"
                    fill="#10b981"
                    radius={[4, 4, 0, 0]}
                    barSize={30}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill="#10b981"
                        className="hover:opacity-80 transition-opacity"
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Total Received</div>
                <div className="text-2xl font-bold text-blue-600">
                  {adminWeekOverviewResponse.totals_last_7_days.received}
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Total Completed</div>
                <div className="text-2xl font-bold text-green-600">
                  {adminWeekOverviewResponse.totals_last_7_days.completed}
                </div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Still Pending</div>
                <div className="text-2xl font-bold text-yellow-600">
                  {(adminWeekOverviewResponse.totals_last_7_days.received - adminWeekOverviewResponse.totals_last_7_days.completed) > 0 ?
                    (adminWeekOverviewResponse.totals_last_7_days.received - adminWeekOverviewResponse.totals_last_7_days.completed) : 0}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* {adminWeekOverviewResponse?.days && ( */}
        <div className="bg-white rounded-lg shadow-sm p-5 mb-6 border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-800">AI Analytics</h2>
          </div>

          <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <Clock className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700">
                Analytics will unlock after 10 orders
              </p>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: '40%' }}></div>
              </div>
            </div>
          </div>
        </div>
        {/* )} */}

        {/* Rest of your existing components */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Sales Persons Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">Sales Persons</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">First Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Last Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {usersListResponse?.sales_persons?.map((person) => (
                    <tr key={person.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800">{person.first_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">{person.last_name}</td>
                      <td className="py-3 px-4">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={person.is_active}
                            onChange={() => togglePersonStatus(person)}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                        </label>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-800"><button
                        onClick={() => navigate(`/sales-person-record/${person.user_pk}`)}
                        className="inline-flex items-center px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                      >
                        View
                      </button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Music Creators Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">Music Creators</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">First Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Last Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {usersListResponse?.music_creators?.map((creator) => (
                    <tr key={creator.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800">{creator.first_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">{creator.last_name}</td>
                      <td className="py-3 px-4">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={creator.is_active}
                            onChange={() => togglePersonStatus(creator)}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
            <button
              onClick={handleViewMoreOrders}
              className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-300"
            >
              View More
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned to
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ordersData?.slice(0, 5).map((order, index) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {order.order_id || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 max-w-[120px] truncate">
                        {order.company_name || order.brand_company_name || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 max-w-[100px] truncate" title={order.contact_person || "-"}>
                        {(order.contact_person && order.contact_person.length > 20)
                          ? `${order.contact_person.substring(0, 20)}...`
                          : (order.contact_person || "-")}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(order.mobile_number && order.mobile_number.length > 20)
                          ? `${order.mobile_number.substring(0, 20)}...`
                          : (order.mobile_number || "-")}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[150px] truncate" title={order.email || "-"}>
                      {order.email || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[150px] truncate" title={order.assigned_to || "-"}>
                      {/* {getAssignedToName(order.assigned_to)} */}
                      <select
                        value={order.assigned_to || ""}
                        onChange={(e) => assignTask(order.order_id, e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white w-full"
                      >
                        <option value="" disabled>Assign to</option>
                        {musicCreators?.data?.map((creator) => (
                          <option key={creator.user_pk} value={creator.user_pk}>
                            {creator.first_name} {creator.last_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/my-orders/${order.order_id}`)}
                          className="inline-flex items-center px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                        >
                          View
                        </button>
                        {adminId.includes(user?.user_id) &&
                          <button
                            onClick={() => copyToClipboard(`https://digikorejingles.com/jingle-order/${order.order_id}`)}
                            className="inline-flex items-center px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                          >
                            Copy Form Link
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>

  );
}

export default AdminDashboard;